import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IssueToPreheat, SimilarIssue} from '@github-ui/issue-create/SimilarIssues'
import {IssueViewer} from '@github-ui/issue-viewer/IssueViewer'
import {ISSUE_VIEWER_DEFAULT_CONFIG} from '@github-ui/issue-viewer/OptionConfig'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {useCachePreheatIssueViewerQueries} from '@github-ui/issue-viewer/UseCachePreheatIssueViewer'
import {noop} from '@github-ui/noop'
import {ScreenFullIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useCallback, useState} from 'react'

import {LABELS} from '../constants/labels'
import {IssueSidePanel} from './show/IssueSidePanel'

type SidePanelHandlers = {
  onSimilarIssueClick?: (issue: SimilarIssue) => void
  onIssuesToPreheat?: (issues: IssueToPreheat[]) => void
}

type SimilarIssueSidePanelProps = {
  repository: {owner: string; name: string} | undefined
  children: (handlers: SidePanelHandlers) => React.ReactNode
}

// Stable empty handlers object when feature is disabled.
const noopHandlers: SidePanelHandlers = {}

/**
 * Wraps children with side panel functionality for previewing similar issues during issue creation.
 * Colocates the side panel state and rendering, exposing a handlers object to children.
 *
 * Uses a render prop pattern so consumers can pass the handlers to IssueCreatePage.
 * When the feature flag is disabled, passes a stable empty handlers object to children and skips side panel rendering.
 */
export function SimilarIssueSidePanel({repository, children}: SimilarIssueSidePanelProps) {
  const isEnabled = isFeatureEnabled('semantic_similarity_duplicate_issue_detection')

  if (!isEnabled) {
    return <>{children(noopHandlers)}</>
  }

  return <SimilarIssueSidePanelEnabled repository={repository}>{children}</SimilarIssueSidePanelEnabled>
}

type SimilarIssueSidePanelEnabledProps = {
  repository: {owner: string; name: string} | undefined
  children: (handlers: SidePanelHandlers) => React.ReactNode
}

// Extracted to avoid conditional hook calls when feature is disabled.
function SimilarIssueSidePanelEnabled({repository, children}: SimilarIssueSidePanelEnabledProps) {
  const [sidePanelItemIdentifier, setSidePanelItemIdentifier] = useState<ItemIdentifier | null>(null)
  const [issuesToPreheat, setIssuesToPreheat] = useState<IssueToPreheat[]>([])
  useCachePreheatIssueViewerQueries({issues: issuesToPreheat, source: 'SimilarIssues'})

  const onSimilarIssueClick = useCallback(
    (issue: SimilarIssue) => {
      if (!repository) return
      setSidePanelItemIdentifier({
        owner: repository.owner,
        repo: repository.name,
        number: issue.number,
        type: 'Issue',
      })
    },
    [repository],
  )

  const onClose = useCallback(() => {
    setSidePanelItemIdentifier(null)
  }, [])

  const handlers: SidePanelHandlers = {
    onSimilarIssueClick,
    onIssuesToPreheat: setIssuesToPreheat,
  }

  const sidePanelItemURL = sidePanelItemIdentifier
    ? `/${sidePanelItemIdentifier.owner}/${sidePanelItemIdentifier.repo}/issues/${sidePanelItemIdentifier.number}`
    : undefined

  return (
    <>
      {children(handlers)}
      {sidePanelItemIdentifier && (
        <IssueSidePanel onClose={onClose}>
          <IssueViewer
            itemIdentifier={sidePanelItemIdentifier}
            optionConfig={{
              ...ISSUE_VIEWER_DEFAULT_CONFIG,
              shouldSkipSetDocumentTitle: true,
              onClose,
              insideSidePanel: true,
              tabbedTimeline: isFeatureEnabled('issues_react_timeline_tabs'),
              // Note: onSubIssueClick/onParentIssueActivate/onIssueDelete are intentionally
              // omitted as navigating sub-issues from similar issues preview is not supported yet
              onIssueUpdate: noop,
              onRemovedFromProject: noop,
              navigateBack: onClose,
              additionalHeaderActions: (
                <IconButton
                  as="a"
                  variant="invisible"
                  icon={ScreenFullIcon}
                  aria-label={LABELS.sidePanelTooltip}
                  href={sidePanelItemURL}
                  target="_blank"
                />
              ),
            }}
          />
        </IssueSidePanel>
      )}
    </>
  )
}
